import { useState, useEffect } from 'react';
import { Tab } from '@headlessui/react';
import {
  InformationCircleIcon,
  BookOpenIcon,
  ScaleIcon,
  GlobeAltIcon,
  HeartIcon,
  HomeIcon,
  CurrencyDollarIcon,
  CpuChipIcon,
  BeakerIcon,
} from '@heroicons/react/24/outline';
import type { Species } from '../api/species';
import { IUCNStatus, CITESStatus } from '../utils/enums';
import { useGlobalConfig } from '../contexts/GlobalConfigContext';
import { hydrateSpeciesForm, buildSpeciesUpdatePayload } from '../utils/speciesFormMapping';
import { formatAiLastUpdated } from '../utils/speciesDisplayUtils';
import SpeciesMutationCatalogPanel from './genetics/SpeciesMutationCatalogPanel';
import TabbedFormActions, { isNonEmptyString, isValidPositiveNumber } from './ui/TabbedFormActions';
import {
  BrandModal,
  BrandModalBody,
  BrandModalHeader,
} from './ui/BrandModal';
import AmountInput from './ui/AmountInput';

interface EditSpeciesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onEdit: (id: string, speciesData: Partial<Species>) => void;
  species: Species;
}

type FormData = {
  commonName: string;
  genus: string;
  species: string;
  subspecies: string;
  domain: string;
  kingdom: string;
  phylum: string;
  class: string;
  family: string;
  order: string;
  typeCategory: string;
  originLocation: string;
  size: string;
  weight: string;
  colorDescription: string;
  captiveBreedingStatus: string;
  longevity: string;
  recommendedHousing: string;
  diet: string;
  nestBoxSize: string;
  clutchSize: string;
  fledgingAge: string;
  hatchingPeriod: string;
  daysBeforeBanding: string;
  bandSize: string;
  iucnStatus: IUCNStatus;
  citesStatus: CITESStatus;
  mutations: string;
  
  // Breeding fields
  breedingSeasonStart: string;
  breedingSeasonEnd: string;
  incubationPeriod: string;
  nestingBehavior: string;
  parentalCarePeriod: string;
  sexualMaturityAge: string;
  breedingPairCompatibility: string;
  nestingMaterialPreferences: string;
  breedingDifficultyLevel: string;
  geneticDiversityConcerns: string;
  
  // Health & Care fields
  lifespan: string;
  dietRequirements: string;
  housingRequirements: string;
  commonHealthIssues: string;
  vaccinationRequirements: string;
  parasiteSusceptibility: string;
  stressFactors: string;
  environmentalRequirements: string;
  socialBehavior: string;
  handlingGuidelines: string;
  quarantinePeriod: string;
  veterinaryCareNeeds: string;
  
  // Market & Sales fields
  marketValueRange: string;
  marketDemandLevel: string;
  legalStatus: string;
  exportRestrictions: string;
  marketTrends: string;
  priceRangeMin: string;
  priceRangeMax: string;
  buyerPreferences: string;
  competitionLevel: string;
  seasonalDemandPatterns: string;
  
  // AI Metadata fields
  aiDataSources: string;
  aiConfidenceScore: string;
  aiExtractionNotes: string;
  manualReviewStatus: string;
  aiProcessingVersion: string;
  lastAiUpdate: string;
};

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

export default function EditSpeciesModal({ isOpen, onClose, onEdit, species }: EditSpeciesModalProps) {
  const { config } = useGlobalConfig();
  const [selectedTab, setSelectedTab] = useState(0);
  const [formData, setFormData] = useState<FormData>({
    commonName: '',
    genus: '',
    species: '',
    subspecies: '',
    domain: '',
    kingdom: '',
    phylum: '',
    class: '',
    family: '',
    order: '',
    typeCategory: '',
    originLocation: '',
    size: '',
    weight: '',
    colorDescription: '',
    captiveBreedingStatus: '',
    longevity: '',
    recommendedHousing: '',
    diet: '',
    nestBoxSize: '',
    clutchSize: '',
    fledgingAge: '',
    hatchingPeriod: '',
    daysBeforeBanding: '',
    bandSize: '',
    iucnStatus: IUCNStatus.LEAST_CONCERN,
    citesStatus: CITESStatus.NOT_LISTED,
    mutations: '',
    
    // Breeding fields
    breedingSeasonStart: '',
    breedingSeasonEnd: '',
    incubationPeriod: '',
    nestingBehavior: '',
    parentalCarePeriod: '',
    sexualMaturityAge: '',
    breedingPairCompatibility: '',
    nestingMaterialPreferences: '',
    breedingDifficultyLevel: '',
    geneticDiversityConcerns: '',
    
    // Health & Care fields
    lifespan: '',
    dietRequirements: '',
    housingRequirements: '',
    commonHealthIssues: '',
    vaccinationRequirements: '',
    parasiteSusceptibility: '',
    stressFactors: '',
    environmentalRequirements: '',
    socialBehavior: '',
    handlingGuidelines: '',
    quarantinePeriod: '',
    veterinaryCareNeeds: '',
    
    // Market & Sales fields
    marketValueRange: '',
    marketDemandLevel: '',
    legalStatus: '',
    exportRestrictions: '',
    marketTrends: '',
    priceRangeMin: '',
    priceRangeMax: '',
    buyerPreferences: '',
    competitionLevel: '',
    seasonalDemandPatterns: '',
    
    // AI Metadata fields
    aiDataSources: '',
    aiConfidenceScore: '',
    aiExtractionNotes: '',
    manualReviewStatus: '',
    aiProcessingVersion: '',
    lastAiUpdate: '',
  });

  useEffect(() => {
    if (species) {
      setFormData((prev) => ({ ...prev, ...hydrateSpeciesForm(species) }));
    }
  }, [species]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    // Handle enum values
    if (name === 'iucnStatus') {
      setFormData((prev) => ({ ...prev, [name]: value as IUCNStatus }));
    } else if (name === 'citesStatus') {
      setFormData((prev) => ({ ...prev, [name]: value as CITESStatus }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onEdit(species.id, buildSpeciesUpdatePayload(formData));
    onClose();
  };

  const categories = [
    {
      name: 'Basic Info',
      icon: InformationCircleIcon,
      content: <BasicInfoTab formData={formData} handleChange={handleChange} />
    },
    {
      name: 'Taxonomy',
      icon: BookOpenIcon,
      content: <TaxonomyTab formData={formData} handleChange={handleChange} />
    },
    {
      name: 'Physical Traits',
      icon: ScaleIcon,
      content: <PhysicalTraitsTab formData={formData} handleChange={handleChange} />
    },
    {
      name: 'Conservation',
      icon: GlobeAltIcon,
      content: <ConservationTab formData={formData} handleChange={handleChange} />
    },
    {
      name: 'Care Guide',
      icon: HeartIcon,
      content: <CareGuideTab formData={formData} handleChange={handleChange} />
    },
    {
      name: 'Breeding',
      icon: HomeIcon,
      content: <BreedingTab formData={formData} handleChange={handleChange} />
    },
    {
      name: 'Health & Care',
      icon: HeartIcon,
      content: <HealthCareTab formData={formData} handleChange={handleChange} />
    },
    {
      name: 'Market & Sales',
      icon: CurrencyDollarIcon,
              content: <MarketSalesTab formData={formData} handleChange={handleChange} config={config} />
    },
    {
      name: 'Genetics',
      icon: BeakerIcon,
      content: (
        <SpeciesMutationCatalogPanel
          speciesId={species.id}
          onMutationsSynced={(names) => {
            setFormData((prev) => ({ ...prev, mutations: names.join(', ') }));
          }}
        />
      ),
    },
    {
      name: 'AI Metadata',
      icon: CpuChipIcon,
      content: <AiMetadataTab formData={formData} handleChange={handleChange} />
    }
  ];

  const canSubmit =
    [
      formData.commonName,
      formData.genus,
      formData.species,
      formData.typeCategory,
      formData.originLocation,
      formData.colorDescription,
      formData.captiveBreedingStatus,
      formData.recommendedHousing,
      formData.diet,
      formData.nestBoxSize,
      formData.bandSize,
      formData.iucnStatus,
      formData.citesStatus,
    ].every(isNonEmptyString) &&
    [
      formData.size,
      formData.weight,
      formData.longevity,
      formData.clutchSize,
      formData.fledgingAge,
      formData.hatchingPeriod,
      formData.daysBeforeBanding,
    ].every(isValidPositiveNumber);

  return (
    <BrandModal open={isOpen} onClose={onClose} maxWidthClass="max-w-6xl">
      <BrandModalHeader title={`Edit Species: ${species.commonName}`} onClose={onClose} />
      <BrandModalBody>
        <form onSubmit={handleSubmit} className="w-full">
          <div className="w-full">
            <Tab.Group selectedIndex={selectedTab} onChange={setSelectedTab}>
              <Tab.List className="flex space-x-1 rounded-xl bg-gray-100 p-1 mb-6">
                {categories.map((category) => (
                  <Tab
                    key={category.name}
                    className={({ selected }) =>
                      classNames(
                        'w-full rounded-lg py-2.5 px-3 text-sm font-medium leading-5 flex items-center justify-center space-x-2',
                        'ring-white ring-opacity-60 ring-offset-2 ring-offset-indigo-400 focus:outline-none focus:ring-2',
                        selected
                          ? 'bg-white shadow text-cyan-700 border border-indigo-200'
                          : 'text-slate-600 hover:bg-white/[0.8] hover:text-gray-800'
                      )
                    }
                  >
                    <category.icon className="h-4 w-4" />
                    <span className="hidden sm:block">{category.name}</span>
                  </Tab>
                ))}
              </Tab.List>
              <Tab.Panels className="mt-2">
                {categories.map((category, idx) => (
                  <Tab.Panel
                    key={idx}
                    className={classNames(
                      'rounded-xl bg-white p-6 shadow-sm border min-h-[400px]',
                      'ring-white ring-opacity-60 ring-offset-2 ring-offset-indigo-400 focus:outline-none focus:ring-2'
                    )}
                  >
                    {category.content}
                  </Tab.Panel>
                ))}
              </Tab.Panels>
            </Tab.Group>
          </div>

          <TabbedFormActions
            onCancel={onClose}
            tabCount={categories.length}
            selectedTab={selectedTab}
            onTabChange={setSelectedTab}
            canSubmit={canSubmit}
            submitLabel="Save Changes"
          />
        </form>
      </BrandModalBody>
    </BrandModal>
  );
}

// Tab Components
interface TabProps {
  formData: FormData;
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
}

interface MarketSalesTabProps extends TabProps {
  config: any;
}

function BasicInfoTab({ formData, handleChange }: TabProps) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-slate-900 mb-4">Basic Information</h3>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label htmlFor="commonName" className="block text-sm font-medium text-slate-700">
              Common Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="commonName"
              id="commonName"
              value={formData.commonName}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              required
              placeholder="e.g., Canary, Cockatiel, Zebra Finch"
            />
          </div>

          <div>
            <label htmlFor="typeCategory" className="block text-sm font-medium text-slate-700">
              Type Category <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="typeCategory"
              id="typeCategory"
              value={formData.typeCategory}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              required
              placeholder="e.g., Finch, Parrot, Canary"
            />
          </div>

          <div>
            <label htmlFor="originLocation" className="block text-sm font-medium text-slate-700">
              Origin Location <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="originLocation"
              id="originLocation"
              value={formData.originLocation}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              required
              placeholder="e.g., Australia, South America, Africa"
            />
          </div>

          <div>
            <label htmlFor="captiveBreedingStatus" className="block text-sm font-medium text-slate-700">
              Captive Breeding Status <span className="text-red-500">*</span>
            </label>
            <select
              name="captiveBreedingStatus"
              id="captiveBreedingStatus"
              value={formData.captiveBreedingStatus}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              required
            >
              <option key="empty-option-1753461297307" value="">Select status</option>
              <option key="common-1753461297307" value="common">Common</option>
              <option key="uncommon-1753461297307" value="uncommon">Uncommon</option>
              <option key="rare-1753461297307" value="rare">Rare</option>
              <option key="very_rare-1753461297307" value="very_rare">Very Rare</option>
            </select>
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700">
          Known Mutations
        </label>
        <p className="mt-1 text-sm text-slate-600">
          {formData.mutations?.trim()
            ? formData.mutations
            : 'No catalog loaded — use the Genetics tab to seed reference mutations.'}
        </p>
        <p className="mt-2 text-sm text-slate-500">
          Mutation names are synced automatically from the structured catalog in the Genetics tab.
        </p>
      </div>
    </div>
  );
}

function TaxonomyTab({ formData, handleChange }: TabProps) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-slate-900 mb-4">Scientific Classification</h3>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label htmlFor="domain" className="block text-sm font-medium text-slate-700">Domain</label>
            <input type="text" name="domain" id="domain" value={formData.domain} onChange={handleChange} className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm" placeholder="Eukarya" />
          </div>
          <div>
            <label htmlFor="kingdom" className="block text-sm font-medium text-slate-700">Kingdom</label>
            <input type="text" name="kingdom" id="kingdom" value={formData.kingdom} onChange={handleChange} className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm" placeholder="Animalia" />
          </div>
          <div>
            <label htmlFor="phylum" className="block text-sm font-medium text-slate-700">Phylum</label>
            <input type="text" name="phylum" id="phylum" value={formData.phylum} onChange={handleChange} className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm" placeholder="Chordata" />
          </div>
          <div>
            <label htmlFor="class" className="block text-sm font-medium text-slate-700">Class</label>
            <input type="text" name="class" id="class" value={formData.class} onChange={handleChange} className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm" placeholder="Aves" />
          </div>
          <div>
            <label htmlFor="order" className="block text-sm font-medium text-slate-700">Order</label>
            <input type="text" name="order" id="order" value={formData.order} onChange={handleChange} className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm" placeholder="e.g., Passeriformes" />
          </div>
          <div>
            <label htmlFor="family" className="block text-sm font-medium text-slate-700">Family</label>
            <input type="text" name="family" id="family" value={formData.family} onChange={handleChange} className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm" placeholder="e.g., Fringillidae" />
          </div>
          <div>
            <label htmlFor="genus" className="block text-sm font-medium text-slate-700">
              Genus <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="genus"
              id="genus"
              value={formData.genus}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              required
              placeholder="e.g., Serinus, Nymphicus, Taeniopygia"
            />
          </div>

          <div>
            <label htmlFor="species" className="block text-sm font-medium text-slate-700">
              Species <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="species"
              id="species"
              value={formData.species}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              required
              placeholder="e.g., canaria, hollandicus, guttata"
            />
          </div>

          <div className="sm:col-span-2">
            <label htmlFor="subspecies" className="block text-sm font-medium text-slate-700">
              Subspecies
            </label>
            <input
              type="text"
              name="subspecies"
              id="subspecies"
              value={formData.subspecies}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              placeholder="Leave blank if no subspecies"
            />
            <p className="mt-2 text-sm text-slate-500">
              Enter subspecies name if applicable, otherwise leave blank.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function PhysicalTraitsTab({ formData, handleChange }: TabProps) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-slate-900 mb-4">Physical Characteristics</h3>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label htmlFor="size" className="block text-sm font-medium text-slate-700">
              Size (cm) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              step="0.1"
              name="size"
              id="size"
              value={formData.size}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              required
              placeholder="e.g., 12.5"
            />
          </div>

          <div>
            <label htmlFor="weight" className="block text-sm font-medium text-slate-700">
              Weight (g) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              step="0.1"
              name="weight"
              id="weight"
              value={formData.weight}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              required
              placeholder="e.g., 25.0"
            />
          </div>

          <div>
            <label htmlFor="longevity" className="block text-sm font-medium text-slate-700">
              Longevity (years) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="longevity"
              id="longevity"
              value={formData.longevity}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              required
              placeholder="e.g., 10"
            />
          </div>
        </div>

        <div className="mt-6">
          <label htmlFor="colorDescription" className="block text-sm font-medium text-slate-700">
            Color Description <span className="text-red-500">*</span>
          </label>
          <textarea
            name="colorDescription"
            id="colorDescription"
            value={formData.colorDescription}
            onChange={handleChange}
            rows={4}
            className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
            required
            placeholder="Describe the typical coloration and markings of this species..."
          />
          <p className="mt-2 text-sm text-slate-500">
            Provide a detailed description of the species' typical colors and markings.
          </p>
        </div>
      </div>
    </div>
  );
}

function ConservationTab({ formData, handleChange }: TabProps) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-slate-900 mb-4">Conservation Status</h3>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label htmlFor="iucnStatus" className="block text-sm font-medium text-slate-700">
              IUCN Status <span className="text-red-500">*</span>
            </label>
            <select
              name="iucnStatus"
              id="iucnStatus"
              value={formData.iucnStatus}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              required
            >
              <option key="empty-option-1753461297307" value="">Select status</option>
              <option key="EX-1753461297307" value="EX">Extinct (EX)</option>
              <option key="EW-1753461297307" value="EW">Extinct in the Wild (EW)</option>
              <option key="CR-1753461297307" value="CR">Critically Endangered (CR)</option>
              <option key="EN-1753461297307" value="EN">Endangered (EN)</option>
              <option key="VU-1753461297307" value="VU">Vulnerable (VU)</option>
              <option key="NT-1753461297307" value="NT">Near Threatened (NT)</option>
              <option key="LC-1753461297307" value="LC">Least Concern (LC)</option>
              <option key="DD-1753461297307" value="DD">Data Deficient (DD)</option>
              <option key="NE-1753461297307" value="NE">Not Evaluated (NE)</option>
            </select>
          </div>

          <div>
            <label htmlFor="citesStatus" className="block text-sm font-medium text-slate-700">
              CITES Status <span className="text-red-500">*</span>
            </label>
            <select
              name="citesStatus"
              id="citesStatus"
              value={formData.citesStatus}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              required
            >
              <option key="empty-option-1753461297307" value="">Select status</option>
              <option key="I-1753461297307" value="I">Appendix I - Most endangered</option>
              <option key="II-1753461297307" value="II">Appendix II - Regulated trade</option>
              <option key="III-1753461297307" value="III">Appendix III - Restricted by country</option>
              <option key="NL-1753461297307" value="NL">Not Listed</option>
            </select>
          </div>
        </div>

        <div className="mt-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
          <h4 className="text-sm font-medium text-yellow-900 mb-2">Conservation Information</h4>
          <p className="text-sm text-yellow-700">
            <strong>IUCN:</strong> International Union for Conservation of Nature status indicates the species' risk of extinction.
          </p>
          <p className="text-sm text-yellow-700 mt-2">
            <strong>CITES:</strong> Convention on International Trade in Endangered Species regulates international trade.
          </p>
        </div>
      </div>
    </div>
  );
}

function CareGuideTab({ formData, handleChange }: TabProps) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-slate-900 mb-4">Care Requirements</h3>
        <div className="space-y-6">
          <div>
            <label htmlFor="recommendedHousing" className="block text-sm font-medium text-slate-700">
              Recommended Housing <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="recommendedHousing"
              id="recommendedHousing"
              value={formData.recommendedHousing}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              required
              placeholder="e.g., Flight cage minimum 120cm x 60cm x 80cm"
            />
            <p className="mt-2 text-sm text-slate-500">
              Specify the minimum housing requirements for this species.
            </p>
          </div>

          <div>
            <label htmlFor="diet" className="block text-sm font-medium text-slate-700">
              Diet <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="diet"
              id="diet"
              value={formData.diet}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              required
              placeholder="e.g., High-quality seed mix, fresh vegetables, occasional fruits"
            />
            <p className="mt-2 text-sm text-slate-500">
              Describe the optimal diet for this species.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function BreedingTab({ formData, handleChange }: TabProps) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-slate-900 mb-4">Breeding Information</h3>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label htmlFor="nestBoxSize" className="block text-sm font-medium text-slate-700">
              Nest Box Size <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="nestBoxSize"
              id="nestBoxSize"
              value={formData.nestBoxSize}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              required
              placeholder="e.g., 12cm x 12cm x 15cm"
            />
          </div>

          <div>
            <label htmlFor="clutchSize" className="block text-sm font-medium text-slate-700">
              Clutch Size <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="clutchSize"
              id="clutchSize"
              value={formData.clutchSize}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              required
              placeholder="e.g., 4"
            />
          </div>

          <div>
            <label htmlFor="hatchingPeriod" className="block text-sm font-medium text-slate-700">
              Hatching / Incubation (days) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="hatchingPeriod"
              id="hatchingPeriod"
              value={formData.hatchingPeriod}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              required
              placeholder="e.g., 14"
            />
          </div>

          <div>
            <label htmlFor="fledgingAge" className="block text-sm font-medium text-slate-700">
              Fledging Age (days) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="fledgingAge"
              id="fledgingAge"
              value={formData.fledgingAge}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              required
              placeholder="e.g., 18"
            />
          </div>

          <div>
            <label htmlFor="daysBeforeBanding" className="block text-sm font-medium text-slate-700">
              Days Before Banding
            </label>
            <input
              type="number"
              name="daysBeforeBanding"
              id="daysBeforeBanding"
              value={formData.daysBeforeBanding}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              placeholder="e.g., 10"
            />
          </div>

          <div>
            <label htmlFor="bandSize" className="block text-sm font-medium text-slate-700">
              Band Size <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="bandSize"
              id="bandSize"
              value={formData.bandSize}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              required
              placeholder="e.g., 4.5mm"
            />
          </div>

          <div>
            <label htmlFor="breedingSeasonStart" className="block text-sm font-medium text-slate-700">
              Breeding Season Start
            </label>
            <input
              type="text"
              name="breedingSeasonStart"
              id="breedingSeasonStart"
              value={formData.breedingSeasonStart}
              onChange={handleChange}
              placeholder="e.g., Spring, March"
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
            />
          </div>

          <div>
            <label htmlFor="breedingSeasonEnd" className="block text-sm font-medium text-slate-700">
              Breeding Season End
            </label>
            <input
              type="text"
              name="breedingSeasonEnd"
              id="breedingSeasonEnd"
              value={formData.breedingSeasonEnd}
              onChange={handleChange}
              placeholder="e.g., Summer, August"
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
            />
          </div>

          <div>
            <label htmlFor="incubationPeriod" className="block text-sm font-medium text-slate-700">
              Incubation Period (days)
            </label>
            <input
              type="number"
              name="incubationPeriod"
              id="incubationPeriod"
              value={formData.incubationPeriod}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              placeholder="e.g., 18"
            />
          </div>

          <div>
            <label htmlFor="nestingBehavior" className="block text-sm font-medium text-slate-700">
              Nesting Behavior
            </label>
            <input
              type="text"
              name="nestingBehavior"
              id="nestingBehavior"
              value={formData.nestingBehavior}
              onChange={handleChange}
              placeholder="e.g., Cavity nester, Ground nester"
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
            />
          </div>

          <div>
            <label htmlFor="parentalCarePeriod" className="block text-sm font-medium text-slate-700">
              Parental Care Period (days)
            </label>
            <input
              type="number"
              name="parentalCarePeriod"
              id="parentalCarePeriod"
              value={formData.parentalCarePeriod}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              placeholder="e.g., 30"
            />
          </div>

          <div>
            <label htmlFor="sexualMaturityAge" className="block text-sm font-medium text-slate-700">
              Sexual Maturity Age (months)
            </label>
            <input
              type="number"
              name="sexualMaturityAge"
              id="sexualMaturityAge"
              value={formData.sexualMaturityAge}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              placeholder="e.g., 180"
            />
          </div>

          <div>
            <label htmlFor="breedingPairCompatibility" className="block text-sm font-medium text-slate-700">
              Breeding Pair Compatibility
            </label>
            <input
              type="text"
              name="breedingPairCompatibility"
              id="breedingPairCompatibility"
              value={formData.breedingPairCompatibility}
              onChange={handleChange}
              placeholder="e.g., Monogamous, Polygamous"
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
            />
          </div>

          <div>
            <label htmlFor="nestingMaterialPreferences" className="block text-sm font-medium text-slate-700">
              Nesting Material Preferences
            </label>
            <input
              type="text"
              name="nestingMaterialPreferences"
              id="nestingMaterialPreferences"
              value={formData.nestingMaterialPreferences}
              onChange={handleChange}
              placeholder="e.g., Twigs, Grass, Feathers"
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
            />
          </div>

          <div>
            <label htmlFor="breedingDifficultyLevel" className="block text-sm font-medium text-slate-700">
              Breeding Difficulty Level
            </label>
            <input
              type="text"
              name="breedingDifficultyLevel"
              id="breedingDifficultyLevel"
              value={formData.breedingDifficultyLevel}
              onChange={handleChange}
              placeholder="e.g., Easy, Moderate, Difficult"
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
            />
          </div>

          <div className="sm:col-span-2">
            <label htmlFor="geneticDiversityConcerns" className="block text-sm font-medium text-slate-700">
              Genetic Diversity Concerns
            </label>
            <textarea
              name="geneticDiversityConcerns"
              id="geneticDiversityConcerns"
              value={formData.geneticDiversityConcerns}
              onChange={handleChange}
              rows={3}
              placeholder="Describe any genetic diversity concerns or breeding recommendations"
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
            />
          </div>
        </div>

        <div className="mt-6 p-4 bg-cyan-50 rounded-lg border border-blue-200">
          <h4 className="text-sm font-medium text-blue-900 mb-2">Breeding Timeline</h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm text-cyan-700">
            <div>
              <strong>Incubation:</strong>{' '}
              {formData.incubationPeriod || formData.hatchingPeriod || '~'} days
            </div>
            <div>
              <strong>Fledging:</strong> {formData.fledgingAge || '~'} days
            </div>
            <div>
              <strong>Clutch Size:</strong> {formData.clutchSize || '~'} eggs
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 

function HealthCareTab({ formData, handleChange }: TabProps) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-slate-900 mb-4">Health & Care Information</h3>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label htmlFor="lifespan" className="block text-sm font-medium text-slate-700">
              Longevity (years)
            </label>
            <input
              type="number"
              name="lifespan"
              id="lifespan"
              value={formData.lifespan}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm bg-slate-50"
              placeholder="Synced from Physical Traits"
              readOnly
            />
            <p className="mt-1 text-xs text-slate-500">Edit longevity under Physical Traits.</p>
          </div>

          <div>
            <label htmlFor="quarantinePeriod" className="block text-sm font-medium text-slate-700">
              Quarantine Period (days)
            </label>
            <input
              type="number"
              name="quarantinePeriod"
              id="quarantinePeriod"
              value={formData.quarantinePeriod}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              placeholder="e.g., 30"
            />
          </div>

          <div>
            <label htmlFor="dietRequirements" className="block text-sm font-medium text-slate-700">
              Diet (saved to Care Guide)
            </label>
            <input
              type="text"
              name="dietRequirements"
              id="dietRequirements"
              value={formData.dietRequirements}
              onChange={handleChange}
              placeholder="e.g., High protein diet, fresh fruits"
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
            />
          </div>

          <div>
            <label htmlFor="housingRequirements" className="block text-sm font-medium text-slate-700">
              Housing (saved to Care Guide)
            </label>
            <input
              type="text"
              name="housingRequirements"
              id="housingRequirements"
              value={formData.housingRequirements}
              onChange={handleChange}
              placeholder="e.g., Large flight cage, natural perches"
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
            />
          </div>

          <div>
            <label htmlFor="commonHealthIssues" className="block text-sm font-medium text-slate-700">
              Common Health Issues
            </label>
            <input
              type="text"
              name="commonHealthIssues"
              id="commonHealthIssues"
              value={formData.commonHealthIssues}
              onChange={handleChange}
              placeholder="e.g., Respiratory infections, feather plucking"
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
            />
          </div>

          <div>
            <label htmlFor="vaccinationRequirements" className="block text-sm font-medium text-slate-700">
              Vaccination Requirements
            </label>
            <input
              type="text"
              name="vaccinationRequirements"
              id="vaccinationRequirements"
              value={formData.vaccinationRequirements}
              onChange={handleChange}
              placeholder="e.g., Annual checkup, specific vaccines"
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
            />
          </div>

          <div>
            <label htmlFor="parasiteSusceptibility" className="block text-sm font-medium text-slate-700">
              Parasite Susceptibility
            </label>
            <input
              type="text"
              name="parasiteSusceptibility"
              id="parasiteSusceptibility"
              value={formData.parasiteSusceptibility}
              onChange={handleChange}
              placeholder="e.g., Mites, worms, protozoa"
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
            />
          </div>

          <div>
            <label htmlFor="stressFactors" className="block text-sm font-medium text-slate-700">
              Stress Factors
            </label>
            <input
              type="text"
              name="stressFactors"
              id="stressFactors"
              value={formData.stressFactors}
              onChange={handleChange}
              placeholder="e.g., Loud noises, sudden changes"
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
            />
          </div>

          <div>
            <label htmlFor="environmentalRequirements" className="block text-sm font-medium text-slate-700">
              Environmental Requirements
            </label>
            <input
              type="text"
              name="environmentalRequirements"
              id="environmentalRequirements"
              value={formData.environmentalRequirements}
              onChange={handleChange}
              placeholder="e.g., Temperature range, humidity"
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
            />
          </div>

          <div>
            <label htmlFor="socialBehavior" className="block text-sm font-medium text-slate-700">
              Social Behavior
            </label>
            <input
              type="text"
              name="socialBehavior"
              id="socialBehavior"
              value={formData.socialBehavior}
              onChange={handleChange}
              placeholder="e.g., Flock bird, solitary"
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
            />
          </div>

          <div>
            <label htmlFor="handlingGuidelines" className="block text-sm font-medium text-slate-700">
              Handling Guidelines
            </label>
            <input
              type="text"
              name="handlingGuidelines"
              id="handlingGuidelines"
              value={formData.handlingGuidelines}
              onChange={handleChange}
              placeholder="e.g., Gentle approach, avoid sudden movements"
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
            />
          </div>

          <div className="sm:col-span-2">
            <label htmlFor="veterinaryCareNeeds" className="block text-sm font-medium text-slate-700">
              Handling / veterinary notes
            </label>
            <textarea
              name="veterinaryCareNeeds"
              id="veterinaryCareNeeds"
              value={formData.veterinaryCareNeeds}
              onChange={handleChange}
              rows={3}
              placeholder="Describe specific veterinary care requirements"
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function MarketSalesTab({ formData, handleChange, config }: MarketSalesTabProps) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-slate-900 mb-4">Market & Sales Information</h3>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label htmlFor="marketValueRange" className="block text-sm font-medium text-slate-700">
              Price range summary
            </label>
            <input
              type="text"
              name="marketValueRange"
              id="marketValueRange"
              value={formData.marketValueRange}
              readOnly
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm bg-slate-50"
              placeholder="Set min/max below"
            />
          </div>

          <div>
            <label htmlFor="marketDemandLevel" className="block text-sm font-medium text-slate-700">
              Market Demand Level
            </label>
            <input
              type="text"
              name="marketDemandLevel"
              id="marketDemandLevel"
              value={formData.marketDemandLevel}
              onChange={handleChange}
              placeholder="e.g., High, Medium, Low"
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
            />
          </div>

          <div>
            <label htmlFor="legalStatus" className="block text-sm font-medium text-slate-700">
              Export / legal restrictions
            </label>
            <input
              type="text"
              name="legalStatus"
              id="legalStatus"
              value={formData.legalStatus}
              onChange={handleChange}
              placeholder="Saved to export restrictions"
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
            />
          </div>

          <div>
            <label htmlFor="exportRestrictions" className="block text-sm font-medium text-slate-700">
              Export Restrictions
            </label>
            <input
              type="text"
              name="exportRestrictions"
              id="exportRestrictions"
              value={formData.exportRestrictions}
              onChange={handleChange}
              placeholder="e.g., CITES restrictions, country-specific"
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
            />
          </div>

          <div>
            <label htmlFor="priceRangeMin" className="block text-sm font-medium text-slate-700">
              Price Range Min ({config?.currency?.default || 'ZAR'})
            </label>
            <AmountInput
              id="priceRangeMin"
              name="priceRangeMin"
              value={formData.priceRangeMin}
              onChange={(v) =>
                handleChange({
                  target: { name: 'priceRangeMin', value: v != null ? String(v) : '' },
                } as React.ChangeEvent<HTMLInputElement>)
              }
              allowEmpty
              placeholder="e.g., 100"
            />
          </div>

          <div>
            <label htmlFor="priceRangeMax" className="block text-sm font-medium text-slate-700">
              Price Range Max ({config?.currency?.default || 'ZAR'})
            </label>
            <AmountInput
              id="priceRangeMax"
              name="priceRangeMax"
              value={formData.priceRangeMax}
              onChange={(v) =>
                handleChange({
                  target: { name: 'priceRangeMax', value: v != null ? String(v) : '' },
                } as React.ChangeEvent<HTMLInputElement>)
              }
              allowEmpty
              placeholder="e.g., 500"
            />
          </div>

          <div>
            <label htmlFor="buyerPreferences" className="block text-sm font-medium text-slate-700">
              Buyer Preferences
            </label>
            <input
              type="text"
              name="buyerPreferences"
              id="buyerPreferences"
              value={formData.buyerPreferences}
              onChange={handleChange}
              placeholder="e.g., Hand-raised, specific mutations"
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
            />
          </div>

          <div>
            <label htmlFor="competitionLevel" className="block text-sm font-medium text-slate-700">
              Competition Level
            </label>
            <input
              type="text"
              name="competitionLevel"
              id="competitionLevel"
              value={formData.competitionLevel}
              onChange={handleChange}
              placeholder="e.g., High, Medium, Low"
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
            />
          </div>

          <div>
            <label htmlFor="seasonalDemandPatterns" className="block text-sm font-medium text-slate-700">
              Seasonal Demand Patterns
            </label>
            <input
              type="text"
              name="seasonalDemandPatterns"
              id="seasonalDemandPatterns"
              value={formData.seasonalDemandPatterns}
              onChange={handleChange}
              placeholder="e.g., Higher demand in spring"
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
            />
          </div>

          <div className="sm:col-span-2">
            <label htmlFor="marketTrends" className="block text-sm font-medium text-slate-700">
              Market Trends
            </label>
            <textarea
              name="marketTrends"
              id="marketTrends"
              value={formData.marketTrends}
              onChange={handleChange}
              rows={3}
              placeholder="Describe current market trends and future outlook"
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function AiMetadataTab({ formData, handleChange }: TabProps) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-slate-900 mb-4">AI Metadata</h3>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label htmlFor="aiDataSources" className="block text-sm font-medium text-slate-700">
              AI Data Sources
            </label>
            <input
              type="text"
              name="aiDataSources"
              id="aiDataSources"
              value={formData.aiDataSources}
              onChange={handleChange}
              placeholder="e.g., parrots.org, birdsoftheworld.org"
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
            />
          </div>

          <div>
            <label htmlFor="aiConfidenceScore" className="block text-sm font-medium text-slate-700">
              AI Confidence Score
            </label>
            <input
              type="number"
              step="0.1"
              min="0"
              max="1"
              name="aiConfidenceScore"
              id="aiConfidenceScore"
              value={formData.aiConfidenceScore}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              placeholder="e.g., 0.85"
            />
          </div>

          <div>
            <label htmlFor="manualReviewStatus" className="block text-sm font-medium text-slate-700">
              Manual Review Status
            </label>
            <input
              type="text"
              name="manualReviewStatus"
              id="manualReviewStatus"
              value={formData.manualReviewStatus}
              onChange={handleChange}
              placeholder="e.g., Pending, Reviewed, Approved"
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
            />
          </div>

          <div>
            <label htmlFor="aiProcessingVersion" className="block text-sm font-medium text-slate-700">
              AI Processing Version
            </label>
            <input
              type="text"
              name="aiProcessingVersion"
              id="aiProcessingVersion"
              value={formData.aiProcessingVersion}
              onChange={handleChange}
              placeholder="e.g., v1.0, GPT-4o"
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
            />
          </div>

          <div>
            <label htmlFor="lastAiUpdate" className="block text-sm font-medium text-slate-700">
              Last AI Update
            </label>
            <input
              type="text"
              name="lastAiUpdate"
              id="lastAiUpdate"
              value={formData.lastAiUpdate ? formatAiLastUpdated({ lastAiUpdate: formData.lastAiUpdate }) : ''}
              readOnly
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm bg-slate-50"
            />
          </div>

          <div className="sm:col-span-2">
            <label htmlFor="aiExtractionNotes" className="block text-sm font-medium text-slate-700">
              AI Extraction Notes
            </label>
            <textarea
              name="aiExtractionNotes"
              id="aiExtractionNotes"
              value={formData.aiExtractionNotes}
              onChange={handleChange}
              rows={4}
              placeholder="Notes about AI data extraction process, confidence levels, and any issues encountered"
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
            />
          </div>
        </div>

        <div className="mt-6 p-4 bg-cyan-50 rounded-lg border border-blue-200">
          <h4 className="text-sm font-medium text-blue-900 mb-2">AI Processing Information</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm text-cyan-700">
            <div>
              <strong>Confidence Score:</strong> {formData.aiConfidenceScore ? `${(parseFloat(formData.aiConfidenceScore) * 100).toFixed(1)}%` : 'Not set'}
            </div>
            <div>
              <strong>Review Status:</strong> {formData.manualReviewStatus || 'Not set'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 